<script setup lang="ts">
import { globalKeys } from 'assets/data/global-keys';
import type { IGameUrlWrapperProps } from '~/interfaces/components/game-url-wrapper';

const props = withDefaults(defineProps<IGameUrlWrapperProps>(), {
  component: 'direct',
  launchAsDemoPlay: false,
  query: () => ({}),
});

const auth = useAuthStore();
const gameStore = useGameStore();
const rgStore = useRgStore();
const siteStore = useSiteStore();

const { $enabled, $trackEvent } = useNuxtApp();
const { $storageService } = useServices();

const route = useRoute();

const { url, fromDemoPlay, fromFavorites, fromGoldenCircle, fromProvider } =
  useLaunchGameUrl({
    game: props.game,
    restore: props.component === 'restore-game-session',
    isSearch: props.component === 'search',
    launchAsDemoPlay: props.launchAsDemoPlay,
    query: props.query,
  });

const generatedSource = computed(() => {
  if (fromFavorites) return `${props.component}-favorites`;
  if (fromGoldenCircle) return `${props.component}-winners-circle`;
  if (fromProvider) return `${props.component}-provider`;
  if (fromDemoPlay) return `${props.component}-free-play`;
  return props.component;
});

const gameUnavailable = computed(() => {
  if (props.game?.unavailable) return true;
  return gameStore.returnDisabledProviders.includes(props.game?.provider);
});

const routeLink = computed(() => (auth.getCanLaunch ? url : route.fullPath));

const clickHandler = (event: MouseEvent) => {
  if (gameUnavailable.value) return;
  if (siteStore.isWebview) {
    useLaunchGamePostMessage(props.game.id);
    return;
  }

  if (siteStore.modalActive) {
    siteStore.deactivateModal();
  }
  if (!auth.getCanLaunch) {
    event.preventDefault();
    if (!auth.getIsUserCompliant && !$enabled('myaccount.enablepartialreg')) {
      auth.setAccountModalPage('document-verification');
    }

    if (!rgStore?.restrictionPermissions?.allowedWager) {
      siteStore.activateModal('accountRestricted');
    }
    return;
  }

  if (props.launchAsDemoPlay)
    $trackEvent('GameEvent', 'LAUNCH_DEMO_GAME', {
      game_name: props?.game?.name,
      game_id: props?.game?.id,
      game_alias: props?.game?.alias,
      category_name: route?.params?.category || 'all',
      vertical_name: route?.params?.contentType || 'home',
      path: route?.fullPath,
      component: props?.component,
    });
  else
    $trackEvent('GameEvent', 'LAUNCH_REAL_GAME', {
      game_name: props?.game?.name,
      game_id: props?.game?.id,
      game_alias: props?.game?.alias,
      category_name: route?.params?.category || 'all',
      vertical_name: route?.params?.contentType || 'home',
      msisdn: auth?.currentUser?.username,
      path: route?.fullPath,
      component: props?.component,
    });

  if (route.fullPath === '/' && auth.isLoggedIn) {
    $storageService.setCookie({
      key: globalKeys.navigationKeys.lastPosition(auth?.userId),
      data: window.scrollY,
      expiry: 1,
    });
  }

  $storageService.setSessionStorage({
    key: globalKeys.navigationKeys.gameLaunchComponent,
    data: generatedSource.value,
  });

  window?.scroll({
    top: 0,
    behavior: 'smooth',
  });
};

defineExpose({
  url,
  generatedSource,
  gameUnavailable,
  clickHandler,
});
</script>

<template>
  <NuxtLink
    :to="routeLink"
    :aria-label="game.name"
    draggable="false"
    @click="clickHandler"
  >
    <slot />
  </NuxtLink>
</template>

<style scoped lang="scss"></style>
